import jsPDF from "jspdf";
import "jspdf-autotable";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";
import dayjs from "dayjs";
import CurrencyFormat from "../../../../hook/CurrencyFormat";

// ─── helpers ────────────────────────────────────────────────
const str = (v) => (v === null || v === undefined ? "" : String(v));
const fmt = (v, cf) => {
  if (!v) return "";
  return str(
    cf
      ? cf(v)
      : Number(v || 0).toLocaleString("th-TH", { minimumFractionDigits: 2 }),
  );
};

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  const M = [
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];
  return `${d.date()} ${M[d.month()]} ${d.year() + 543}`;
};

// วาด checkmark จริงใน cell (ใช้ใน didDrawCell)
const drawCellCheckmark = (pdf, cell) => {
  const cx = cell.x + cell.width / 2;
  const cy = cell.y + cell.height / 2;
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.6);
  pdf.line(cx - 2, cy, cx - 0.3, cy + 2);
  pdf.line(cx - 0.3, cy + 2, cx + 2.5, cy - 1.8);
};

// ─── main export ────────────────────────────────────────────
export const generateClearPayReport = ({ dataList, fromDate, toDate }) => {
  const [
    currencyFormat,
    currencyFormatComma,
    currencyFormatPoint,
    currencyFormatNoPoint,
  ] = CurrencyFormat();
  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const setF = (style = "normal", size = 13) => {
    pdf.setFont("THSarabunNew", style);
    pdf.setFontSize(size);
  };

  const pageW = 297;
  const mL = 5;
  const mR = 5;
  const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
  const fFrom = fromDate ? dayjs(fromDate).format("DD/MM/YYYY") : "";
  const fTo = toDate ? dayjs(toDate).format("DD/MM/YYYY") : "";

  let pageNum = 1;

  const drawCompanyHeader = () => {
    setF("bold", 14);
    pdf.text("บริษัท ซีเอแอล ลิสซิ่ง จำกัด", pageW - mR, 8, { align: "right" });
    setF("normal", 10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(
      "1/20-24 ถ.มิตรภาพ ต.ในเมือง อ.เมือง จ.ขอนแก่น 40000",
      pageW - mR,
      13,
      { align: "right" },
    );
    pdf.text("เลขประจำตัวผู้เสียภาษี", pageW - mR, 18, { align: "right" });
    pdf.setTextColor(0, 0, 0);

    const gradH = 22;
    const steps = 40;
    const gradX = 0;
    const gradW = pageW;
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const r = Math.round(255 - (255 - 235) * t);
      const g = Math.round(255 - (255 - 240) * t);
      const b = 255;

      pdf.setFillColor(r, g, b);
      pdf.rect((pageW / steps) * i, 2, pageW / steps + 0.5, gradH, "F");
    }

    for (let i = 0; i < steps; i++) {
      const t = i / steps; // 0 → 1
      const r = Math.round(255 - (255 - 235) * t); // 255 → 235
      const g = Math.round(255 - (255 - 240) * t); // 255 → 240
      const b = 255; // 255 → 255 (คงที่)

      pdf.setFillColor(r, g, b);
      pdf.rect(gradX + (gradW / steps) * i, 2, gradW / steps + 0.5, gradH, "F");
    }

    // re-draw text on top of rect
    setF("bold", 14);
    pdf.text("บริษัท ซีเอแอล ลิสซิ่ง จำกัด", pageW - mR, 8, { align: "right" });
    setF("normal", 10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(
      "1/20-24 ถ.มิตรภาพ ต.ในเมือง อ.เมือง จ.ขอนแก่น 40000",
      pageW - mR,
      13,
      { align: "right" },
    );
    pdf.text("เลขประจำตัวผู้เสียภาษี", pageW - mR, 18, { align: "right" });
    pdf.setTextColor(0, 0, 0);
  };

  // ── header ──────────────────────────────────────────────
  const drawPageHeader = () => {
    drawCompanyHeader();
    setF("bold", 18);
    pdf.text("รายงานเคลียร์เงินทดรองจ่าย", mL, 30);
    setF("normal", 13);
    pdf.text(`จากวันที่ ${fFrom}  ถึงวันที่ ${fTo}`, mL, 36);
    setF("normal", 11);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`วันที่พิมพ์ : ${now}`, mL, 42);
    pdf.text(`หน้าที่ : ${pageNum}`, pageW - mR, 44, { align: "right" });
    pdf.setTextColor(0, 0, 0);
    pdf.setDrawColor(0, 0, 180);
    pdf.setLineWidth(0.4);
    pdf.line(mL, 38, pageW - mR, 38);
  };

  // ── กรอบ 4 ด้าน ─────────────────────────────────────────
  const drawTableBorder = (startY, endY) => {
    pdf.setDrawColor(0, 0, 180);
    pdf.setLineWidth(0.25);
    pdf.line(mL, startY, pageW - mR, startY); // บน
    pdf.line(mL, endY, pageW - mR, endY); // ล่าง
    pdf.line(mL, startY, mL, endY); // ซ้าย
    pdf.line(pageW - mR, startY, pageW - mR, endY); // ขวา
  };

  // ── Marker คงไว้สำหรับ คืนเงิน / เบิกเพิ่ม ──────────────
  const MK_RETURN = "MK_RETURN";
  const MK_EXTRA = "MK_EXTRA";

  const body = dataList.map((r, i) => {
    const netpay = parseFloat(r.NETPAY ?? 0);
    const totpay = parseFloat(r.TOTPAY ?? 0);
    const advpay = parseFloat(r.ADVPAYAMT ?? 0);
    // rawDiff: บวก = คืนเงิน, ลบ = เบิกเพิ่ม
    const rawDiff = netpay !== 0 ? netpay : advpay - totpay;

    const isReturn = rawDiff > 0;
    const isExtra = rawDiff < 0;
    const diff = rawDiff;

    const isCash = r.CHKFLAG === "1";
    const isTransfer = r.CHKFLAG === "2";

    const nameText = r.NAME_LAWYER
      ? `${r.NAME_LAWYER}${r.OFFCODE ? ` (${r.OFFCODE})` : ""}`
      : r.USERID || "";

    // ✅ ยอด diff ใส่ในคอลัมน์ตาม CHKFLAG
    // ถ้าเป็นเบิกเพิ่ม (isExtra) ใช้ prefix "NEG:" เพื่อแสดงสีแดง
    const diffStr = isExtra
      ? `NEG:-${currencyFormatPoint(diff)}`
      : currencyFormatPoint(diff);

    return [
      r._isFirst && r._seqNo ? r._seqNo : "",
      str(fmtDate(r.DOCDT)), // 1
      str(r.DOCNO || ""), // 2
      str(fmtDate(r.INPUTDT || r.DOCDT)), // 3
      str(r.DESCRIPTION || r.MEMO1 || ""), // 4
      str(nameText), // 5
      currencyFormatPoint(r.AMOUNT ?? totpay), // 6
      currencyFormatPoint(advpay), // 7
      isReturn ? MK_RETURN : "", // 8  คืนเงิน ✓
      isExtra ? MK_EXTRA : "", // 9  เบิกเพิ่ม ✓
      isCash ? diffStr : "", // 10 เงินสด
      isTransfer ? diffStr : "", // 11 เงินโอน
    ];
  });

  // ── แถวรวม ────────────────────────────────────────────────
  const sumAmt = dataList.reduce(
    (s, r) => s + parseFloat(r.AMOUNT ?? r.TOTPAY ?? 0),
    0,
  );
  const sumAdv = dataList.reduce((s, r) => s + parseFloat(r.ADVPAYAMT ?? 0), 0);

  // รวมแยก เงินสด / เงินโอน จาก rawDiff
  const sumCash = dataList
    .filter((r) => r.CHKFLAG === "1")
    .reduce((s, r) => {
      const n = parseFloat(r.NETPAY ?? 0);
      const raw =
        n !== 0 ? n : parseFloat(r.ADVPAYAMT ?? 0) - parseFloat(r.TOTPAY ?? 0);
      return s + raw;
    }, 0);

  const sumTransfer = dataList
    .filter((r) => r.CHKFLAG === "2")
    .reduce((s, r) => {
      const n = parseFloat(r.NETPAY ?? 0);
      const raw =
        n !== 0 ? n : parseFloat(r.ADVPAYAMT ?? 0) - parseFloat(r.TOTPAY ?? 0);
      return s + raw;
    }, 0);

  const makeSumStr = (val) =>
    val < 0 ? `NEG:${currencyFormatPoint(val)}` : currencyFormatPoint(val);

  body.push([
    "",
    "",
    "",
    "",
    "",
    "รวมยอดจ่ายสุทธิ", // 5
    currencyFormatPoint(sumAmt), // 6
    currencyFormatPoint(sumAdv), // 7
    "",
    "", // 8, 9
    makeSumStr(sumCash), // 10 รวมเงินสด
    makeSumStr(sumTransfer), // 11 รวมเงินโอน
  ]);

  // ── วาดหน้าแรก ────────────────────────────────────────────

  drawPageHeader();
  const tableStartY = 45;
  const pageInfo = {};
  const pageH = pdf.internal.pageSize.getHeight();

  pdf.autoTable({
    head: [
      [
        "No.",
        "วันที่เอกสาร",
        "เลขที่เอกสาร",
        "วันที่ขอเบิก",
        "รายละเอียดการจ่าย",
        "ผู้เคลียร์",
        "จำนวนเงิน",
        "ยอดเบิก",
        "คืนเงิน",
        "เบิกเพิ่ม",
        "เงินสด",
        "เงินโอน",
      ],
    ],
    body,
    margin: { left: mL, right: mR, top: 45 },
    startY: tableStartY,
    styles: {
      font: "THSarabunNew",
      fontSize: 12,
      cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 },
      lineWidth: 0,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [240, 235, 255],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 12,
      halign: "center",
      lineWidth: 0,
    },
    bodyStyles: { minCellHeight: 8, lineWidth: 0 },
    tableLineWidth: 0,
    columnStyles: {
      0: { halign: "center", cellWidth: 10 },
      1: { halign: "center", cellWidth: 20 },
      2: { halign: "left", cellWidth: 25 },
      3: { halign: "center", cellWidth: 20 },
      4: { halign: "left", cellWidth: 45 },
      5: { halign: "left", cellWidth: 45 },
      6: { halign: "right", cellWidth: 22 },
      7: { halign: "right", cellWidth: 22 },
      8: { halign: "center", cellWidth: 14 },
      9: { halign: "center", cellWidth: 14 },
      10: { halign: "center", cellWidth: 25 },
      11: { halign: "center", cellWidth: 25 },
    },

    didParseCell(data) {
      const isTotal = data.row.index === body.length - 1;
      if (data.section === "body") {
        if (isTotal) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [240, 235, 255];
        }

        const raw = data.cell.raw;
        if (typeof raw !== "string") return;

        // ซ่อน marker คืนเงิน/เบิกเพิ่ม → วาดใน didDrawCell
        if (raw === MK_RETURN || raw === MK_EXTRA) {
          data.cell.text = [""];
        }

        // ✅ col 10, 11 — NEG: แสดงสีแดง
        if (
          (data.column.index === 10 || data.column.index === 11) &&
          raw.startsWith("NEG:")
        ) {
          data.cell.text = [raw.replace("NEG:", "")];
          data.cell.styles.textColor = [220, 0, 0];
          if (!isTotal) data.cell.styles.fontStyle = "bold";
        }
      }
    },

    didDrawCell(data) {
      if (data.section === "body") {
        const raw = data.cell.raw;
        if (raw === MK_RETURN || raw === MK_EXTRA) {
          drawCellCheckmark(pdf, data.cell);
        }

        // ✅ วาดเส้นบนแถวรวมยอด
        const isTotal = data.row.index === body.length - 1;
        if (isTotal) {
          pdf.setDrawColor(0, 0, 180);
          pdf.setLineWidth(0.25);
          pdf.line(
            data.cell.x,
            data.cell.y,
            data.cell.x + data.cell.width,
            data.cell.y,
          );
        }
      }
    },

    didDrawPage(data) {
      const pn = pdf.internal.getCurrentPageInfo().pageNumber;
      const sY = pn === 1 ? tableStartY : data.settings.margin.top;
      pageInfo[pn] = { sY }; // ✅ เก็บแค่ sY ก่อน

      if (pn > pageNum) {
        pageNum = pn;
        drawPageHeader();
      }
    },
  });

  // ✅ วาดกรอบทุกหน้า หลัง autoTable เสร็จ (รู้ finalY แล้ว)
  const finalY = pdf.lastAutoTable.finalY;
  const totalPages = pdf.internal.getNumberOfPages();
  const marginBottom = 10;

  Object.entries(pageInfo).forEach(([pnStr, { sY }]) => {
    const pn = parseInt(pnStr);
    const isLast = pn === totalPages;
    const eY = isLast ? finalY : pageH - marginBottom;

    pdf.setPage(pn); // ✅ สลับไปหน้านั้นก่อนวาด
    pdf.setDrawColor(0, 0, 180);
    pdf.setLineWidth(0.25);
    pdf.line(mL, sY, pageW - mR, sY); // บน
    pdf.line(mL, sY, mL, eY); // ซ้าย
    pdf.line(pageW - mR, sY, pageW - mR, eY); // ขวา
    pdf.line(mL, eY, pageW - mR, eY); // ล่าง (ทุกหน้า ถูกต้องแล้ว)
  });

  // ── เปิด PDF ────────────────────────────────────────────
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  const win = window.open(url);
  if (win) win.onload = () => win.print();
  else alert("กรุณาปิดการบล็อกป๊อปอัปเพื่อใช้งานฟังก์ชันนี้");
};
